/*
 * section-faq-sidebar.js — Slice 18 Phase C.
 *
 * Sidebar-specific behaviors layered on top of Phase A's
 * component-shared-accordions.js (which handles disclosure toggle).
 * Mirrors assets/section-faq.js (Phase B) — only DOM hooks + the
 * mobile-collapse dropdown handler differ.
 *
 * Behaviors:
 *   1. Nav generation — generateNav(section) scans .faq-category divs
 *      and injects per-category <button> elements into [data-faq-nav]
 *      container after the static "All" button. Sums per-category
 *      counts to update [data-faq-nav-count-all]. Replaces the
 *      forbidden section.blocks iteration (Phase B torn-down attempt;
 *      DEV-S18-PHASE-B-2026-05-28 hotfix). Click delegation operates
 *      at document level so newly-injected buttons work without
 *      rebinding.
 *   2. Search filter — input event on [data-faq-search]; toggles
 *      .is-hidden on [data-acc-item] based on question+answer
 *      textContent substring match (case-insensitive). Hides
 *      .faq-category when all items hidden. Shows [data-faq-no-results]
 *      when no items visible. Respects active filter category when
 *      nav_behavior=filter. textContent (not innerText) so closed-state
 *      [hidden] panels still contribute their answer text.
 *   3. Nav interaction — click event delegated on [data-faq-nav-item].
 *      jumplink mode (data-nav-behavior=jumplink): smooth-scroll to
 *      #cat-{anchor}; "all" scrolls to section top.
 *      filter mode (data-nav-behavior=filter): set data-active-cat on
 *      [data-faq-sidebar] inner + .is-active-cat on matching category;
 *      re-run search filter to combine with active cat. If the mobile
 *      dropdown is open, close it on selection.
 *   4. Mobile dropdown — click delegated on [data-faq-nav-toggle]:
 *      toggles data-nav-open on the .faq-sidebar__nav--mobile-collapse
 *      wrap + aria-expanded. CSS shows/hides the navlist based on
 *      data-nav-open at ≤749px only.
 *   5. Init — DOMContentLoaded + shopify:section:load. Scan for
 *      [data-faq-sidebar]; per-instance init runs generateNav BEFORE
 *      attachSearch so nav items exist when search would interact
 *      with them. __faqSidebarInited per-section idempotency guard.
 *
 * IIFE-dedup-guarded per Rule J12. Single document-level click
 * listener handles nav clicks AND nav-toggle clicks (event delegation).
 * No window.<theme> namespace pollution per Rule J8.
 *
 * Rules in force:
 *   Rule J1  — addEventListener only
 *   Rule J7  — document-level listener
 *   Rule J8  — no window.* namespace pollution; pure IIFE
 *   Rule J12 — IIFE-dedup guard for multi-instance safety
 */
(function () {
  if (window.__BLOCKDEDUP_SECTION_FAQ_SIDEBAR__) return;
  window.__BLOCKDEDUP_SECTION_FAQ_SIDEBAR__ = true;

  /* ---------- Nav generation ---------- */

  function generateNav(section) {
    var container = section.querySelector('[data-faq-nav]');
    if (!container) return;

    var allCountSpan = container.querySelector('[data-faq-nav-count-all]');
    var showCounts = !!allCountSpan;

    var categories = section.querySelectorAll('.faq-category');
    var total = 0;

    for (var i = 0; i < categories.length; i++) {
      var cat = categories[i];
      var handle = cat.getAttribute('data-cat-name') || '';
      var anchor = cat.getAttribute('data-cat-anchor') || handle;
      var items = cat.querySelectorAll('[data-acc-item]');
      var count = items.length;
      total += count;

      var nameEl = cat.querySelector('.faq-category__name');
      var prefixEl = cat.querySelector('.faq-category__prefix');
      var name = nameEl ? nameEl.textContent.trim() : handle;
      var prefix = prefixEl ? prefixEl.textContent.trim() : '';

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'faq-sidebar__navlink';
      btn.setAttribute('data-faq-nav-item', handle);
      btn.setAttribute('data-faq-nav-anchor', 'cat-' + anchor);
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', 'false');

      if (prefix.length > 0) {
        var prefixSpan = document.createElement('span');
        prefixSpan.className = 'faq-sidebar__navlink-prefix';
        prefixSpan.setAttribute('aria-hidden', 'true');
        prefixSpan.textContent = prefix;
        btn.appendChild(prefixSpan);
      }

      var nameSpan = document.createElement('span');
      nameSpan.className = 'faq-sidebar__navlink-name';
      nameSpan.textContent = name;
      btn.appendChild(nameSpan);

      if (showCounts) {
        var countSpan = document.createElement('span');
        countSpan.className = 'faq-sidebar__navlink-count';
        countSpan.textContent = String(count);
        btn.appendChild(countSpan);
      }

      container.appendChild(btn);
    }

    if (allCountSpan) {
      allCountSpan.textContent = String(total);
    }
  }

  /* ---------- Search filter ---------- */

  function runSearchFilter(section) {
    var input = section.querySelector('[data-faq-search]');
    var query = input ? input.value.trim().toLowerCase() : '';
    var navBehavior = section.getAttribute('data-nav-behavior');
    var activeCat = section.getAttribute('data-active-cat') || 'all';
    var items = section.querySelectorAll('[data-acc-item]');
    var visibleCount = 0;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var matchesQuery = true;
      if (query.length > 0) {
        var q = item.querySelector('.shared-accordion__question');
        var a = item.querySelector('.shared-accordion__answer');
        var haystack = ((q ? q.textContent : '') + ' ' + (a ? a.textContent : '')).toLowerCase();
        matchesQuery = haystack.indexOf(query) !== -1;
      }

      var matchesCat = true;
      if (navBehavior === 'filter' && activeCat !== 'all') {
        var cat = item.closest('.faq-category');
        var catName = cat ? cat.getAttribute('data-cat-name') : '';
        matchesCat = catName === activeCat;
      }

      if (matchesQuery && matchesCat) {
        item.classList.remove('is-hidden');
        visibleCount++;
      } else {
        item.classList.add('is-hidden');
      }
    }

    var categories = section.querySelectorAll('.faq-category');
    for (var c = 0; c < categories.length; c++) {
      var category = categories[c];
      var visibleItems = category.querySelectorAll('[data-acc-item]:not(.is-hidden)');
      if (visibleItems.length === 0) {
        category.classList.add('is-hidden');
      } else {
        category.classList.remove('is-hidden');
      }
    }

    var noResults = section.querySelector('[data-faq-no-results]');
    if (noResults) {
      if (visibleCount === 0) {
        noResults.removeAttribute('hidden');
      } else {
        noResults.setAttribute('hidden', '');
      }
    }
  }

  function attachSearch(section) {
    var input = section.querySelector('[data-faq-search]');
    if (!input) return;
    input.addEventListener('input', function () { runSearchFilter(section); });
  }

  /* ---------- Nav interaction ---------- */

  function activateNav(section, name) {
    var links = section.querySelectorAll('[data-faq-nav-item]');
    var activeName = '';
    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (link.getAttribute('data-faq-nav-item') === name) {
        link.classList.add('is-active');
        link.setAttribute('aria-selected', 'true');
        var nameEl = link.querySelector('.faq-sidebar__navlink-name');
        activeName = nameEl ? nameEl.textContent.trim() : name;
      } else {
        link.classList.remove('is-active');
        link.setAttribute('aria-selected', 'false');
      }
    }

    var toggleLabel = section.querySelector('[data-faq-nav-toggle-label]');
    if (toggleLabel && activeName) {
      toggleLabel.textContent = activeName;
    }
  }

  function applyFilterToCategories(section, name) {
    var categories = section.querySelectorAll('.faq-category');
    for (var i = 0; i < categories.length; i++) {
      if (name === 'all' || categories[i].getAttribute('data-cat-name') === name) {
        categories[i].classList.add('is-active-cat');
      } else {
        categories[i].classList.remove('is-active-cat');
      }
    }
  }

  function closeMobileDropdown(section) {
    var wrap = section.querySelector('.faq-sidebar__nav--mobile-collapse');
    if (!wrap) return;
    wrap.setAttribute('data-nav-open', 'false');
    var toggle = wrap.querySelector('[data-faq-nav-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
  }

  function onNavClick(e) {
    var link = e.target.closest('[data-faq-nav-item]');
    if (!link) return;
    var section = link.closest('[data-faq-sidebar]');
    if (!section) return;

    var name = link.getAttribute('data-faq-nav-item');
    var navBehavior = section.getAttribute('data-nav-behavior');

    activateNav(section, name);

    if (navBehavior === 'filter') {
      section.setAttribute('data-active-cat', name);
      applyFilterToCategories(section, name);
      runSearchFilter(section);
    } else {
      if (name === 'all') {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        var anchor = link.getAttribute('data-faq-nav-anchor');
        var target = anchor ? section.querySelector('#' + anchor) : null;
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    closeMobileDropdown(section);
  }

  function onNavToggleClick(e) {
    var toggle = e.target.closest('[data-faq-nav-toggle]');
    if (!toggle) return;
    var wrap = toggle.closest('.faq-sidebar__nav--mobile-collapse');
    if (!wrap) return;
    var isOpen = wrap.getAttribute('data-nav-open') === 'true';
    wrap.setAttribute('data-nav-open', isOpen ? 'false' : 'true');
    toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
  }

  /* ---------- Init ---------- */

  function initSection(section) {
    if (section.__faqSidebarInited) return;
    section.__faqSidebarInited = true;
    generateNav(section);
    attachSearch(section);
  }

  function initAll(scope) {
    var root = scope || document;
    var sections = root.querySelectorAll('[data-faq-sidebar]');
    for (var i = 0; i < sections.length; i++) {
      initSection(sections[i]);
    }
  }

  function init() {
    document.addEventListener('click', onNavClick);
    document.addEventListener('click', onNavToggleClick);
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) initAll(e.target);
  });
})();
